// Confirmation Dialog Component
import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { AlertTriangle, Trash2, Info, Loader2 } from 'lucide-react';
import { Button } from './Button';
import { cn } from '@utils/classNames';
import { ModalShell } from './ModalShell';

interface ConfirmationOptions {
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'warning' | 'info';
  onConfirm: () => void | Promise<void>;
  onCancel?: () => void;
}

interface ConfirmationDialog extends ConfirmationOptions {
  id: string;
}

interface ConfirmationContextType {
  confirm: (options: ConfirmationOptions) => void;
}

const ConfirmationContext = createContext<ConfirmationContextType | undefined>(undefined);

export const useConfirm = () => {
  const context = useContext(ConfirmationContext);
  if (!context) {
    throw new Error('useConfirm must be used within ConfirmationProvider');
  }
  return context;
};

export const ConfirmationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [dialogs, setDialogs] = useState<ConfirmationDialog[]>([]);
  const [loadingDialogId, setLoadingDialogId] = useState<string | null>(null);

  const confirm = useCallback((options: ConfirmationOptions) => {
    const id = Math.random().toString(36).substring(7);
    const dialog: ConfirmationDialog = {
      id,
      confirmText: 'Confirm',
      cancelText: 'Cancel',
      variant: 'danger',
      ...options,
    };
    
    setDialogs(prev => [...prev, dialog]);
  }, []);

  const removeDialog = (id: string) => {
    setDialogs(prev => prev.filter(d => d.id !== id));
  };

  const handleConfirm = async (dialog: ConfirmationDialog) => {
    setLoadingDialogId(dialog.id);
    try {
      await dialog.onConfirm();
    } catch (error) {
      console.error('Confirmation action failed:', error);
    } finally {
      setLoadingDialogId(null);
      removeDialog(dialog.id);
    }
  };

  const handleCancel = (dialog: ConfirmationDialog) => {
    if (dialog.onCancel) {
      dialog.onCancel();
    }
    removeDialog(dialog.id);
  };

  const getVariantStyles = (variant: 'danger' | 'warning' | 'info') => {
    switch (variant) {
      case 'danger':
        return {
          icon: Trash2,
          iconColor: 'text-danger',
          iconBg: 'bg-danger/10',
          accent: 'text-danger',
          button: 'bg-danger hover:bg-danger/90 text-white',
        };
      case 'warning':
        return {
          icon: AlertTriangle,
          iconColor: 'text-orange-400',
          iconBg: 'bg-orange-400/10',
          accent: 'text-orange-400',
          button: 'bg-orange-500 hover:bg-orange-600 text-white',
        };
      case 'info':
        return {
          icon: Info,
          iconColor: 'text-blue-400',
          iconBg: 'bg-blue-400/10',
          accent: 'text-blue-400',
          button: 'bg-blue-500 hover:bg-blue-600 text-white',
        };
    }
  };

  return (
    <ConfirmationContext.Provider value={{ confirm }}>
      {children}
      
      {/* Dialogs */}
      {dialogs.map((dialog, index) => {
        const styles = getVariantStyles(dialog.variant!);
        const Icon = styles.icon;
        const isLast = index === dialogs.length - 1;
        const isLoading = loadingDialogId === dialog.id;

        return (
          <ModalShell
            key={dialog.id}
            onClose={() => !loadingDialogId && isLast && handleCancel(dialogs[dialogs.length - 1])}
            closeOnBackdrop={isLast && !loadingDialogId}
            closeOnEscape={isLast && !loadingDialogId}
            panelClassName={cn(
              'w-full max-w-sm sm:max-w-md rounded-xl overflow-hidden animate-slide-in-up',
              !isLast && 'opacity-50 scale-95'
            )}
            zIndexBackdrop={isLast ? 'z-[9200]' : 'z-[9199]'}
            zIndexContainer={isLast ? 'z-[9201]' : 'z-[9200]'}
          >
            <div
              className={cn(
                'pointer-events-auto w-full max-w-sm sm:max-w-md bg-dark-secondary border border-white/10 rounded-xl shadow-2xl overflow-hidden animate-slide-in-up',
                !isLast && 'opacity-50 scale-95'
              )}
              onClick={(e) => e.stopPropagation()}
            >
                  {/* Content */}
                  <div className="p-6 sm:p-8">
                    {/* Icon & Title */}
                    <div className="flex items-start gap-4 mb-4">
                      <div className={cn('flex-shrink-0 p-2.5 rounded-xl', styles.iconBg)}>
                        <Icon className={cn('w-5 h-5', styles.iconColor)} />
                      </div>
                      <div className="flex-1 min-w-0 pt-0.5">
                        <h3 className={cn('font-dela text-lg sm:text-xl text-white mb-2', styles.accent)}>
                          {dialog.title}
                        </h3>
                        <p className="font-inter text-sm text-white/70 leading-relaxed">
                          {dialog.message}
                        </p>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-3 justify-end mt-6">
                      <Button
                        onClick={() => handleCancel(dialog)}
                        variant="secondary"
                        className="px-5 py-2.5 text-sm font-inter font-semibold"
                        disabled={isLoading}
                      >
                        {dialog.cancelText}
                      </Button>
                      <Button
                        onClick={() => handleConfirm(dialog)}
                        disabled={isLoading}
                        className={cn(
                          'px-5 py-2.5 text-sm font-inter font-semibold transition-all shadow-lg flex items-center gap-2',
                          styles.button,
                          isLoading && 'opacity-70 cursor-not-allowed'
                        )}
                      >
                        {isLoading ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            <span>{dialog.confirmText}...</span>
                          </>
                        ) : (
                          dialog.confirmText
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
          </ModalShell>
        );
      })}
    </ConfirmationContext.Provider>
  );
};

